import SearchIcon from "@mui/icons-material/Search";
import { IconButton, Paper } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { PredictiveAddressSearch } from "components/organisations/pav-address-input";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import * as QueryKeys from "data";
import { fetchAddressById } from "data/queries";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

interface AddressSearchProps {
  onSelectAddress: ({
    longitude,
    latitude,
    geocoder_attributes,
  }: {
    longitude: number;
    latitude: number;
    geocoder_attributes: { [key: string]: string };
  }) => void;
  onClearAddress: () => void;
}

export const AddressSearch = (props: AddressSearchProps) => {
  const [expanded, setExpanded] = useState(false);
  const [addressId, setAddressId] = useState("");
  const classes = useStyles();

  const { enqueueQueryFailed, enqueueMutationFailed } = useCustomSnackbars();

  const addressByIdQuery = useQuery({
    queryKey: [QueryKeys.addressById, addressId],
    queryFn: () => fetchAddressById(addressId),
    enabled: !!addressId,
  });

  useEffect(() => {
    if (addressByIdQuery.isSuccess && addressByIdQuery.data) {
      setAddressId("");
      const coordinates = addressByIdQuery.data.address?.geometry?.coordinates;
      const properties = addressByIdQuery.data.address?.properties ?? {};

      // Filter out null/undefined values from properties
      const geocoder_attributes: { [key: string]: string } = {};
      Object.entries(properties).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          geocoder_attributes[key] = value;
        }
      });

      props.onSelectAddress({
        longitude: coordinates?.[0] ?? 0,
        latitude: coordinates?.[1] ?? 0,
        geocoder_attributes,
      });
    }
  }, [addressByIdQuery.isSuccess]);

  useEffect(() => {
    if (addressByIdQuery.isError) {
      enqueueQueryFailed(addressByIdQuery.error.toString());
    }
  }, [addressByIdQuery.isError]);

  return (
    <Paper className={expanded ? classes.expanded : classes.minimized}>
      <IconButton aria-label="search" onClick={() => setExpanded(!expanded)}>
        <SearchIcon />
      </IconButton>
      {expanded && (
        <Formik
          initialValues={{
            address: "",
          }}
          onSubmit={async (values) => {}}
        >
          {({
            values: { address },
            setFieldValue,
            handleChange,
            handleBlur,
            errors,
            touched,
          }) => {
            return (
              <>
                <Form
                  style={{
                    width: "340px",
                    alignSelf: "center",
                    marginBottom: "7px",
                    marginRight: "7px",
                  }}
                >
                  <PredictiveAddressSearch
                    readOnly={false}
                    disabled={false}
                    autoFocus={true}
                    textFieldStyle={{
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        height: "15px",
                      },
                    }}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    setFieldValue={(field: string, value: string) => {
                      if (!value) {
                        props.onClearAddress();
                        return;
                      }
                      if (field === "addressId") {
                        setAddressId(value);
                      }
                    }}
                    id="address"
                    size="small"
                    name="address"
                    label=""
                    value={address}
                    error={
                      address !== null &&
                      touched.address &&
                      Boolean(errors.address)
                    }
                    helperText={touched.address && errors.address}
                    showManualAddressLabel={false}
                    onClearAddress={props.onClearAddress}
                  />
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </Paper>
  );
};

const useStyles = makeStyles({
  expanded: {
    height: "50px",
    // padding: "8px",
    width: "288px",
    alignItems: "center",
    display: "flex",
    justifyContent: "space-around",
    flexDirection: "row",
    alignSelf: "flex-end",
  },
  minimized: {
    width: "40px",
    height: "40px",
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    alignSelf: "flex-end",
  },
});
